import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { Tooltip, TooltipProvider } from '@latitude/tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  BarBreachLevels,
  BarLabelFormatter,
  BarStatus,
  BarTooltipFormatter,
  ProgressBarProps,
} from './types';
import { progressBarFillVariants } from './variants/progress-bar-variants';

const AVG_LETTER_WIDTH = 8;
export const TOTAL_ANIMATION_TIME = 500;

export const ProgressBar = ({
  size = 'medium',
  status = 'default',
  value,
  breachLevels = [{ id: 'default', value: 0 }],
  seamless = false,
  testid,
  bottomLabelColor,
  bottomLabelFormatter,
  topLabelColor,
  topLabelFormatter,
  tooltipFormatter,
}: ProgressBarProps) => {
  const [initial, setInitial] = useState(true);
  const [hasAnimationDelay, setHasAnimationDelay] = useState(true);
  const animationDuration = useMemo(
    () => TOTAL_ANIMATION_TIME / breachLevels.length,
    [breachLevels.length],
  );

  useEffect(() => {
    setTimeout(() => {
      setInitial(false);
    }, 10);

    setTimeout(() => {
      setHasAnimationDelay(false);
    }, TOTAL_ANIMATION_TIME);
  }, []);

  const heightClass = { small: 'h-1', medium: 'h-2', large: 'h-8' }[size];
  const [hoveredSection, setHoveredSection] = useState<number>();
  const sortedBreaches = useMemo(
    () => breachLevels.toSorted((a, b) => a.value - b.value),
    [breachLevels],
  );

  const formattedLevels = useMemo(
    () =>
      sortedBreaches.map((level, index) => {
        const nextLevel = sortedBreaches[index + 1];
        const avgTopTextLength = topLabelFormatter
          ? topLabelFormatter(level, index).length * AVG_LETTER_WIDTH
          : 0;
        const avgBottomTextLength = bottomLabelFormatter
          ? bottomLabelFormatter(level, index).length * AVG_LETTER_WIDTH
          : 0;

        const maxTextLength = Math.max(avgBottomTextLength, avgTopTextLength);

        if (!nextLevel) {
          const fillWidth =
            value >= level.value
              ? ((value - level.value) / (100 - level.value)) * 100
              : 0;

          return {
            ...level,
            fillWidth: isNaN(fillWidth) ? 0 : Math.min(fillWidth, 100),
            width: 100 - level.value,
            maxTextLength,
          };
        }

        const width = nextLevel.value - level.value;
        let fillWidth;
        if (value >= nextLevel.value) {
          fillWidth = 100;
        } else if (value <= level.value) {
          fillWidth = 0;
        } else {
          fillWidth = ((value - level.value) / width) * 100;
        }

        return {
          ...level,
          width,
          fillWidth: Math.min(fillWidth, 100),
          maxTextLength,
        };
      }),
    [value, sortedBreaches],
  );

  const roundedClasses = useCallback(
    (index: number) =>
      seamless
        ? cn(
            index === 0 && 'rounded-l-md',
            index === formattedLevels.length - 1 && 'rounded-r-md',
          )
        : 'rounded-full',
    [seamless, formattedLevels.length],
  );

  const bar = (
    <div className="w-full" data-testid={testid}>
      <div className={cn('flex items-center', !seamless && 'gap-1')}>
        {formattedLevels.map((level, index) => {
          const tooltip = tooltipFormatter?.(level, index);
          const trackLabel = typeof tooltip === 'string' ? tooltip : undefined;
          const track = (
            <div
              role={trackLabel ? 'img' : undefined}
              aria-label={trackLabel}
              className={cn(
                heightClass,
                'bg-color-comp-progress-bar-info-container',
                roundedClasses(index),
              )}
            >
              <div
                className={cn(
                  progressBarFillVariants({ status: level.status ?? status }),
                  'h-full transition-all ease-out',
                  roundedClasses(index),
                )}
                data-testid={`${level.id}-fill`}
                style={{
                  width: `${initial ? 0 : level.status ? 100 : level.fillWidth}%`,
                  transitionDuration: `${animationDuration}ms`,
                  transitionDelay: `${hasAnimationDelay ? index * animationDuration : 0}ms`,
                }}
              />
            </div>
          );

          return (
            <div
              key={level.id}
              className="flex min-w-0 flex-col gap-1 transition-all duration-300"
              onMouseEnter={() => setHoveredSection(index)}
              onMouseLeave={() => setHoveredSection(undefined)}
              data-testid={`${level.id}-section`}
              style={{
                width: `${level.width}%`,
                minWidth:
                  hoveredSection === index
                    ? `${level.maxTextLength}px`
                    : undefined,
              }}
            >
              {topLabelFormatter ? (
                <Text
                  color={topLabelColor}
                  variant="captionMedium"
                  className="truncate"
                >
                  {topLabelFormatter(level, index)}
                </Text>
              ) : null}
              {tooltip === undefined ? (
                track
              ) : (
                <Tooltip text={tooltip} wrapperClassName="w-full">
                  {track}
                </Tooltip>
              )}
              {bottomLabelFormatter ? (
                <Text
                  color={bottomLabelColor}
                  variant="captionRegular"
                  className="truncate"
                >
                  {bottomLabelFormatter(level, index)}
                </Text>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  return tooltipFormatter ? (
    <TooltipProvider delayDuration={300} skipDelayDuration={500}>
      {bar}
    </TooltipProvider>
  ) : (
    bar
  );
};

ProgressBar.displayName = 'ProgressBar';

export {
  type ProgressBarProps,
  type BarLabelFormatter,
  type BarTooltipFormatter,
  type BarBreachLevels,
  type BarStatus,
  progressBarFillVariants,
};
